import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/config/api';

const API = BACKEND_URL;
const STORAGE_KEY = 'fhc_referral';

/**
 * Capture ?ref= param from URL immediately on page load.
 * Runs once at module level so the code is never lost,
 * even if the hook mounts later (e.g. inside a modal).
 */
(function captureRefParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      // Always overwrite localStorage with the fresh URL param
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: ref, name: null, pending: true }));
      // Clean URL without reload
      params.delete('ref');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  } catch { /* SSR / test safety */ }
})();

export function useReferral() {
  const [referralCode, setReferralCode] = useState(null);
  const [affiliateName, setAffiliateName] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      // Read whatever was captured (either by the IIFE above or a previous session)
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) { setLoading(false); return; }

      let parsed;
      try { parsed = JSON.parse(stored); } catch { setLoading(false); return; }

      const code = parsed?.code;
      if (!code) { setLoading(false); return; }

      // If already validated (has name and not pending), use cached data
      if (parsed.name && !parsed.pending) {
        setReferralCode(parsed.code);
        setAffiliateName(parsed.name);
        setLoading(false);
        return;
      }

      // Validate the code via API
      try {
        const res = await fetch(`${API}/api/dashboard/referral/validate/${code}`);
        if (res.ok) {
          const data = await res.json();
          setReferralCode(data.code);
          setAffiliateName(data.affiliate_name);
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: data.code, name: data.affiliate_name }));
        } else {
          // Invalid code — clear storage
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {
        // Network error — use code anyway so it's not lost
        setReferralCode(parsed.code);
        if (parsed.name) setAffiliateName(parsed.name);
      }
      setLoading(false);
    };
    init();
  }, []);

  const clearReferral = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setReferralCode(null);
    setAffiliateName(null);
  }, []);

  return { referralCode, affiliateName, loading, clearReferral };
}
